import React, { forwardRef, useCallback, useEffect, useReducer } from 'react';

import _ from 'lodash';
import { Button, FormControl, FormControlLabel, IconButton } from '@material-ui/core';
import { Modal, MenuItem, Paper, Radio, RadioGroup } from '@material-ui/core';
import { Select, Typography, LinearProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { MdClose } from 'react-icons/md';

import API from 'utils/api';
import { usePropsCallback, useSources } from 'hooks';
import { classes } from 'components/utils';
import { Counter, SectionTitle, Tooltip } from 'components';

import AddConditionDialog from './AddConditionDialog';
import AddSegmentDialog from './AddSegmentDialog';
import DataList from 'routes/Dashboard/Analytics/components/DataList';
import ChatGroupList from 'routes/Dashboard/Analytics/ChatGroupList/ChatGroupList';

import styles from './UserSelector.module.scss';

const initialState = ({ chatFilter, hints }) => {
  const config = [
    {
      label: 'Default',
      hint:
        _.get(hints, 'default') ||
        'New messages will be delivered to anyone who follows this event',
    },
    {
      label: 'Users',
      hint:
        _.get(hints, 'users') ||
        'New messages will be delivered to USERS who follow this event AND meet the conditions',
    },
  ];

  if (chatFilter) {
    config.push({
      label: 'Chats',
      hint:
        _.get(hints, 'chats') ||
        'New messages will only be delivered to CHATS that follow the selected topics',
      connector: 'or',
    });
  }

  return {
    selected: 0,
    connector: 'and',
    conditions: [],
    matches: [],
    loading: true,
    config,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_SELECTED': {
      const selected = _.toNumber(action.payload);
      const config = state.config[selected];

      return selected !== state.selected && config
        ? {
            ...state,
            selected,
            connector: config.connector || 'and',
            conditions: config.conditions || [],
          }
        : state;
    }

    case 'SET_CONNECTOR': {
      const config = state.config[state.selected];
      const connector = action.payload;

      if (config && connector !== state.connector) {
        config.connector = connector;
        return { ...state, connector };
      }

      return state;
    }

    case 'ADD_CONDITION': {
      const config = state.config[state.selected];

      if (config) {
        config.conditions = [...(config.conditions || []), action.payload];
        return { ...state, conditions: config.conditions, dialog: null };
      }

      return state;
    }

    case 'CLEAR_CONDITION': {
      const config = state.config[state.selected];

      if (config && config.conditions) {
        config.conditions = config.conditions.filter(it => it !== action.payload);
        return { ...state, conditions: config.conditions };
      }

      return state;
    }

    case 'SET_CONDITIONS': {
      const config = state.config[state.selected];

      if (config) {
        config.conditions = action.payload || [];
        return { ...state, conditions: config.conditions };
      }

      return state;
    }

    case 'SET_MATCHES':
      return { ...state, matches: action.payload || [], loading: false };

    case 'DIALOG':
      return (state.dialog || null) !== (action.payload || null)
        ? { ...state, dialog: action.payload || null }
        : state;

    case 'LOADING':
      return { ...state, loading: true };

    default:
      return state;
  }
};

const UserSelector = props => {
  const { event } = props;

  const [state, dispatch] = useReducer(reducer, props, initialState);
  const { selected, connector, conditions, matches, config, dialog } = state;

  const sources = useSources();
  const theme = useTheme();
  const onChange = usePropsCallback(props.onChange);

  useEffect(() => {
    if (selected && event._id) {
      dispatch({ type: 'LOADING' });
      const eventId = event._id;

      if (selected === 1) {
        const listConditions = parseConditions(conditions);
        const logicalConnector = connector;

        API.getUsersWithEvent(
          { eventId, listConditions, logicalConnector },
          sources
        ).then(({ data }) => {
          dispatch({ type: 'SET_MATCHES', payload: data });
          onChange({ listConditions, logicalConnector });
        });
      } else {
        const topics = conditions.map(({ value }) => value.id);

        API.getChatsWithTopics(topics, sources).then(({ data }) => {
          dispatch({ type: 'SET_MATCHES', payload: data });
          onChange({ topics });
        });
      }
    } else {
      dispatch({ type: 'SET_MATCHES', payload: [] });
      onChange({});
    }
  }, [selected, connector, conditions, event._id, onChange]);

  const createSegment = useCallback(
    ({ name, description }, connector, conditions) =>
      API.createSegment(
        {
          segmentName: name,
          description: description || undefined,
          deleteRestrict: true,
          segment: {
            conector: connector,
            listConditions: parseConditions(conditions),
          },
        },
        sources
      ).then(({ data }) => {
        if (data) {
          const { tagCategoryId, tagCategoryName, tagName, tagId } = data;

          dispatch({
            type: 'SET_CONDITIONS',
            payload: [
              {
                category: { id: tagCategoryId, text: tagCategoryName },
                operator: 'is',
                value: { id: tagId, text: tagName },
              },
            ],
          });
        }

        dispatch({ type: 'DIALOG' });
      }),
    [sources]
  );

  const renderTitle = () => (
    <div className={styles.title}>
      <SectionTitle title="Message Delivery Mode">
        {props.title || 'Select how new messages will be delivered.'}
      </SectionTitle>

      <div
        className={classes(
          styles.matches,
          selected ? styles.active : '',
          state.loading ? styles.loading : ''
        )}
        onClick={
          matches.length && !state.loading
            ? () => dispatch({ type: 'DIALOG', payload: 'matches' })
            : undefined
        }
        style={matches.length && !state.loading ? { cursor: 'pointer' } : undefined}
      >
        <div>
          <Counter value={matches.length}>
            {value => (
              <Typography className={styles.counter} variant="h5">
                {value}
              </Typography>
            )}
          </Counter>

          <Typography className={styles.label} variant="body2">
            {matches.length > 1 ? 'MATCHES' : 'MATCH'}
          </Typography>
        </div>

        <LinearProgress style={{ visibility: state.loading ? 'visible' : 'hidden' }} />
      </div>
    </div>
  );

  const renderRadio = () => {
    return (
      <FormControl style={{ userSelect: 'none', flexShrink: 0 }}>
        <RadioGroup
          defaultValue={0}
          value={selected}
          onChange={e => dispatch({ type: 'SET_SELECTED', payload: e.target.value })}
        >
          {config.map((it, index) => (
            <FormControlLabel
              key={index}
              value={index}
              control={<CustomRadio hint={it.hint} />}
              label={it.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const renderConnectors = () => (
    <div className={styles.header}>
      <Select
        value={connector}
        onChange={e => dispatch({ type: 'SET_CONNECTOR', payload: e.target.value })}
        disabled={selected !== 1}
      >
        <MenuItem value="and">All of the following conditions</MenuItem>
        <MenuItem value="or">Any of the following conditions</MenuItem>
      </Select>
    </div>
  );

  const renderConditions = () => (
    <div className={styles.body}>
      {conditions.length ? (
        conditions.map((el, index) => {
          const { category, operator, value } = el;

          return (
            <div key={index} className={styles.item}>
              <span>{`${category.text} ${operator} ${value.text}`}</span>

              <IconButton
                className={styles.button}
                onClick={() => dispatch({ type: 'CLEAR_CONDITION', payload: el })}
              >
                <MdClose fontSize="medium" />
              </IconButton>
            </div>
          );
        })
      ) : (
        <Typography variant="caption" className={styles.empty}>
          Click ADD CONDITION to start
        </Typography>
      )}
    </div>
  );

  const renderButtons = () => {
    const btProps = { variant: 'contained', color: 'primary' };

    return (
      <div className={styles.footer}>
        <Button
          {...btProps}
          onClick={() => dispatch({ type: 'DIALOG', payload: 'condition' })}
        >
          Add condition
        </Button>

        {selected === 1 ? (
          <Button
            {...btProps}
            disabled={!conditions.length}
            onClick={() => dispatch({ type: 'DIALOG', payload: 'segment' })}
          >
            Create segment
          </Button>
        ) : null}
      </div>
    );
  };

  const renderContent = () =>
    selected ? (
      <div className={styles.conditions}>
        {renderConnectors()}
        {renderConditions()}
        {renderButtons()}
      </div>
    ) : (
      <div className={styles.default} style={{ minHeight: config.length * 38 }}>
        <Typography variant="body1">{config[0].hint}</Typography>
      </div>
    );

  return (
    <>
      <Paper className={classes(styles.userSelector, styles[theme.name])}>
        {renderTitle()}

        <div className={styles.body}>
          {renderRadio()}
          {renderContent()}
        </div>
      </Paper>

      <AddConditionDialog
        open={dialog === 'condition'}
        mode={selected === 1 ? 'tag' : 'topic'}
        onClose={() => dispatch({ type: 'DIALOG' })}
        onAdd={data => dispatch({ type: 'ADD_CONDITION', payload: data })}
      />

      <AddSegmentDialog
        open={dialog === 'segment'}
        onClose={() => dispatch({ type: 'DIALOG' })}
        onSubmit={data => createSegment(data, connector, conditions)}
      />

      <Modal
        open={dialog === 'matches'}
        onClose={() => dispatch({ type: 'DIALOG' })}
        className={styles.modal}
      >
        <div>
          {selected === 1 ? (
            <DataList listUsersInteraction={matches} />
          ) : (
            <ChatGroupList data={matches} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default UserSelector;

const CustomRadio = forwardRef(({ hint, ...props }, ref) => (
  <Tooltip title={hint} placement="top" enterDelay={800}>
    <Radio ref={ref} {...props} color="primary" size="small" />
  </Tooltip>
));

const parseConditions = conditions =>
  conditions.map(({ category, operator, value }) => ({
    keyId: category.id,
    keyName: category.text,
    operator,
    valueId: value.id,
    valueName: value.text,
  }));
